﻿<script setup lang="ts">
    import { computed, onMounted, ref, watch } from 'vue';
    import DetailsTable from '@/apps/report/DetailsTable.vue';
    import { http } from '@/shared/httpWrapper';
    import {
        genericViewQueryAsText,
        getLibrariesStatistics,
        getLibrariesStatisticsWithScopeFilter,
        getLibraryStatisticDetails,
        getScopes,
        recomputeLibraryStatistics,
        type LibraryStatisticsReadModel,
        type CodeSetStatisticsReadModel,
        type ScopeWithDescription
    } from '@/shared/helpers/api';
    import useShow from '@/shared/composables/useShow';
    import { longDate, shortDate } from '@/formatters';

    const { showError, showInfo } = useShow();

    type LibraryMetadataRow = {
        Name: string;
        IsGrouped: boolean | null;
        IsForStatisticReports: boolean;
    };

    const facilities = ref<ScopeWithDescription[]>([]);
    const selectedFacility = ref<ScopeWithDescription | null>(null);
    const libraryStatsList = ref<LibraryStatisticsReadModel[]>([]);
    const selectedLibrary = ref<LibraryStatisticsReadModel | null>(null);
    const selectedLibraryDetails = ref<CodeSetStatisticsReadModel[]>([]);
    const allLibraryDetails = ref<Record<string, CodeSetStatisticsReadModel[]>>({});
    const libraryMetadata = ref<Record<string, LibraryMetadataRow> | null>(null);
    const showGlobalLibraries = ref(true);
    const showGroupedLibraries = ref(true);
    const showUngroupedLibraries = ref(true);
    const showUnknownGroupingLibraries = ref(true);
    const showLibrariesWithoutDiff = ref(true);
    const loadingLibraryStatistics = ref(false);
    const loadingDetails = ref(false);
    const loadingAllDetails = ref(false);
    const activeDetails = ref<string[]>([]);

    const selectedLibraryMetadata = computed(() => {
        return selectedLibrary.value && libraryMetadata.value
            ? libraryMetadata.value[selectedLibrary.value.libraryName]
            : null;
    });

    const filteredLibraries = computed(() => {
        if (!libraryMetadata.value)
            return [];

        return libraryStatsList.value.filter(row => {
            const meta = libraryMetadata.value![row.libraryName];
            if (meta?.IsForStatisticReports !== true) {
                return false;
            }
            if (!showLibrariesWithoutDiff.value
                && (row.stidMissingInStidCodeCount === 0
                    && row.stidMissingInCommonLibCodeCount === 0
                    && row.stidInBothButDifferentCodeCount === 0)) {
                return false;
            }
            if (showGlobalLibraries.value && row.isGlobal) {
                return true;
            }
            if (!showGroupedLibraries.value && meta?.IsGrouped === true) {
                return false;
            }
            if (!showUngroupedLibraries.value && meta?.IsGrouped === false) {
                return false;
            }
            if (!showUnknownGroupingLibraries.value && (meta === undefined || meta.IsGrouped === null)) {
                return false;
            }
            return true;
        });
    });

    watch(selectedFacility, async () => {
        await loadLibrariesStatistics();
    });

    watch(showLibrariesWithoutDiff, async (newVal) => {
        if (!newVal) {
            await loadDetailsAllLibraries();
        }
    });

    async function loadFacilities(): Promise<void> {
        facilities.value = (await getScopes(http, 'Facility')).sort((a, b) => a.identity.localeCompare(b.identity));
    }

    async function loadLibraryMetadata(): Promise<void> {
        try {
            const res = await genericViewQueryAsText<LibraryMetadataRow>(http, 'FROM LibraryMetadata WHERE IsValid = true');
            libraryMetadata.value = Object.fromEntries(res.data.map(lib => [lib.Name, lib]));
        } catch (error) {
            showError(error);
        }
    }

    async function loadLibrariesStatistics(): Promise<void> {
        try {
            loadingLibraryStatistics.value = true;
            libraryStatsList.value = selectedFacility.value
                ? await getLibrariesStatisticsWithScopeFilter(http, selectedFacility.value.identity)
                : await getLibrariesStatistics(http);
        } catch (error) {
            showError(error);
        } finally {
            loadingLibraryStatistics.value = false;
        }
    }

    async function loadDetailsForSelectedLibrary(): Promise<void> {
        if (selectedLibrary.value !== null) {
            await loadDetails(selectedLibrary.value);
        }
    }

    async function loadDetails(library: LibraryStatisticsReadModel): Promise<void> {
        try {
            loadingDetails.value = true;
            selectedLibrary.value = library;
            selectedLibraryDetails.value = await getLibraryStatisticDetails(http, library.libraryName, false);
        } catch (error) {
            showError(error);
        } finally {
            loadingDetails.value = false;
        }
    }

    async function loadDetailsAllLibraries(): Promise<void> {
        try {
            loadingAllDetails.value = true;
            allLibraryDetails.value = {};
            const promises = filteredLibraries.value.map(library =>
                getLibraryStatisticDetails(http, library.libraryName, true)
                    .then(result => [library.libraryName, result])
            );
            const results = await Promise.all(promises);
            allLibraryDetails.value = Object.fromEntries(results);
        } catch (error) {
            showError(error);
        } finally {
            loadingAllDetails.value = false;
        }
    }

    async function refresh(): Promise<void> {
        await Promise.all([
            loadLibraryMetadata(),
            loadLibrariesStatistics(),
            loadDetailsForSelectedLibrary()
        ]);
    }

    function deselectLibrary(): void {
        selectedLibrary.value = null;
        selectedLibraryDetails.value = [];
    }

    async function recompute(): Promise<void> {
        try {
            selectedLibraryDetails.value = [];
            loadingDetails.value = true;
            await recomputeLibraryStatistics(http, selectedLibrary.value!.libraryName);
            await loadDetails(selectedLibrary.value!);
            showInfo('Statistics re-computed');
        } catch (error) {
            showError(error);
        } finally {
            loadingDetails.value = false;
        }
    }

    function toggleAllDetails(): void {
        if (activeDetails.value.length === 0) {
            activeDetails.value = filteredLibraries.value.map(obj => obj.libraryName);
        } else {
            activeDetails.value = [];
        }
    }

    function rowCssClass(row: LibraryStatisticsReadModel): string | undefined {
        return !row.allIsReleaseReady ? 'not-release-ready' : undefined;
    }

    onMounted(async () => {
        await Promise.all([
            loadFacilities(),
            loadLibraryMetadata(),
            loadLibrariesStatistics()
        ]);
    });
</script>

<template>
    <div>
        <div class="columns">
            <div class="column">
                <h2 class="title is-3">
                    <b-icon
                        icon="table" />
                    Library statistics
                </h2>
            </div>
            <div class="column">
                <div class="buttons is-pulled-right">
                    <b-button
                        v-if="selectedLibrary"
                        icon-left="arrow-left"
                        @click="deselectLibrary">
                        Back to libraries
                    </b-button>
                    <b-button
                        v-if="selectedLibrary"
                        type="is-danger"
                        icon-left="auto-fix"
                        @click="recompute">
                        Recompute
                    </b-button>
                    <b-button
                        icon-left="refresh"
                        @click="refresh">
                        Refresh
                    </b-button>
                </div>
            </div>
        </div>
        <div
            v-if="!selectedLibrary"
            class="block">
            <b-field grouped>
                <b-field
                    label="Facility"
                    label-position="on-border">
                    <b-select
                        v-model="selectedFacility"
                        :disabled="loadingLibraryStatistics">
                        <option
                            v-for="facility in facilities"
                            :key="facility.identity"
                            :value="facility">
                            {{ facility.identity }} - {{ facility.description }}
                        </option>
                    </b-select>
                    <p class="control">
                        <b-button
                            icon-right="eraser"
                            :disabled="!selectedFacility"
                            @click="selectedFacility = null" />
                    </p>
                </b-field>
                <b-switch v-model="showGlobalLibraries">
                    Include global
                </b-switch>
                <b-switch v-model="showGroupedLibraries">
                    Include grouped
                </b-switch>
                <b-switch v-model="showUngroupedLibraries">
                    Include ungrouped
                </b-switch>
                <b-switch v-model="showUnknownGroupingLibraries">
                    Include no grouping info
                </b-switch>
                <b-switch v-model="showLibrariesWithoutDiff">
                    Include libraries without differences
                </b-switch>
                <b-button
                    v-if="!showLibrariesWithoutDiff"
                    custom-class="button"
                    @click="toggleAllDetails">
                    <span>{{ activeDetails.length > 0 ? 'Hide' : 'Open' }} details</span>
                </b-button>
            </b-field>
        </div>
        <p
            v-if="selectedFacility"
            class="block">
            <i>Only showing data for <b>{{ selectedFacility?.identity }}</b>. Deselect facility to show everything.</i>
        </p>
        <div class="block">
            <div class="legend-box not-release-ready">
                &nbsp;
            </div>: Some sets are not release ready
        </div>
        <hr>

        <div class="block">
            <b-table
                v-if="!selectedLibrary"
                :detailed="!showLibrariesWithoutDiff"
                :opened-detailed="activeDetails"
                detail-key="libraryName"
                :data="filteredLibraries"
                :default-sort="['libraryName', 'asc']"
                :striped="false"
                :narrowed="true"
                :hoverable="false"
                :loading="loadingLibraryStatistics"
                :sticky-header="true"
                height="70vh"
                :row-class="rowCssClass">
                <b-table-column
                    v-slot="props"
                    field="libraryName"
                    label="Library"
                    sortable
                    searchable>
                    <span v-if="selectedFacility">
                        {{ props.row.libraryName }}</span>
                    <a
                        v-else
                        @click="loadDetails(props.row)">
                        {{ props.row.libraryName }}
                    </a>
                </b-table-column>
                <b-table-column
                    v-slot="props"
                    field="allLockedForDelete"
                    label=""
                    sortable>
                    <b-tooltip :label="props.row.allLockedForDelete ? 'Locked for delete' : 'Some sets open for delete'">
                        <b-icon
                            size="is-small"
                            type="is-info"
                            :icon="props.row.allLockedForDelete ? 'lock' : 'lock-off'" />
                    </b-tooltip>
                </b-table-column>
                <b-table-column
                    v-slot="props"
                    field="allIsReleaseReady"
                    label=""
                    sortable>
                    <b-tooltip :label="props.row.allIsReleaseReady ? 'Released' : 'Some sets not release ready'">
                        <b-icon
                            size="is-small"
                            type="is-info"
                            :icon="props.row.allIsReleaseReady ? 'cloud' : 'alert'" />
                    </b-tooltip>
                </b-table-column>
                <b-table-column
                    v-slot="props"
                    field="maxStatisticsUpdatedDate"
                    label="Updated"
                    sortable>
                    <b-tooltip
                        multilined
                        :label="'Max:' + props.row.maxStatisticsUpdatedDate + ' Min:' + props.row.minStatisticsUpdatedDate"
                        position="is-right">
                        {{ shortDate(props.row.maxStatisticsUpdatedDate) }}
                    </b-tooltip>
                </b-table-column>
                <b-table-column
                    v-slot="props"
                    field="codeSetCount"
                    label="#sets"
                    numeric
                    sortable>
                    {{ props.row.codeSetCount }}
                </b-table-column>
                <b-table-column
                    v-slot="props"
                    field="codeCount"
                    label="#codes"
                    numeric
                    sortable>
                    {{ props.row.codeCount }}
                </b-table-column>
                <b-table-column
                    v-slot="props"
                    field="validCodeCount"
                    label="#valid"
                    numeric
                    sortable>
                    {{ props.row.validCodeCount }}
                </b-table-column>
                <b-table-column
                    v-slot="props"
                    label="#invalid"
                    numeric>
                    {{ props.row.codeCount - props.row.validCodeCount }}
                </b-table-column>
                <b-table-column
                    v-slot="props"
                    field="stidCodeCount"
                    label="#codes STID"
                    numeric
                    sortable>
                    {{ props.row.stidCodeCount }}
                </b-table-column>
                <b-table-column
                    v-slot="props"
                    field="stidValidCodeCount"
                    label="#valid STID"
                    numeric
                    sortable>
                    {{ props.row.stidValidCodeCount }}
                </b-table-column>
                <b-table-column
                    v-slot="props"
                    field="stidMissingInStidCodeCount"
                    label="#missing STID"
                    numeric
                    sortable>
                    {{ props.row.stidMissingInStidCodeCount }}
                </b-table-column>
                <b-table-column
                    v-slot="props"
                    field="stidMissingInCommonLibCodeCount"
                    label="#missing CL"
                    numeric
                    sortable>
                    {{ props.row.stidMissingInCommonLibCodeCount }}
                </b-table-column>
                <b-table-column
                    v-slot="props"
                    field="stidInBothButDifferentCodeCount"
                    label="#diff w/STID"
                    numeric
                    sortable>
                    {{ props.row.stidInBothButDifferentCodeCount }}
                </b-table-column>
                <b-table-column
                    v-slot="props"
                    field="maxStidSyncDate"
                    label="STID sync"
                    sortable>
                    {{ shortDate(props.row.maxStidSyncDate) }}
                </b-table-column>
                <template #detail="props">
                    <details-table
                        table-height="20rem"
                        :loading-details="loadingAllDetails"
                        :details="allLibraryDetails[props.row.libraryName]"
                        :is-searchable="false" />
                </template>
            </b-table>
        </div>

        <div
            v-if="!selectedLibrary"
            class="block">
            To include statistics for a library, make sure it is registered in <b>LibraryMetadata</b> and that <b>IsForStatisticReports</b> is set to <b>true</b>.
        </div>

        <div v-if="selectedLibrary">
            <h3 class="title is-4">
                <span>Statistics for {{ selectedLibrary.libraryName }}</span>
            </h3>
            <div
                class="columns is-desktop is-multiline">
                <div class="column is-one-third-desktop is-full-tablet is-full-mobile">
                    <div
                        class="box">
                        <table class="table is-striped is-narrow is-fullwidth">
                            <tbody>
                                <tr>
                                    <th>Code count</th>
                                    <td>{{ selectedLibrary.codeCount }} total, {{ selectedLibrary.validCodeCount }} valid, {{ selectedLibrary.codeCount - selectedLibrary.validCodeCount }} not valid</td>
                                </tr>
                                <tr>
                                    <th>Code count STID</th>
                                    <td>{{ selectedLibrary.stidCodeCount }} total, {{ selectedLibrary.stidValidCodeCount }} valid, {{ selectedLibrary.stidCodeCount - selectedLibrary.stidValidCodeCount }} not valid</td>
                                </tr>
                                <tr>
                                    <th>STID sync</th>
                                    <td>
                                        {{ selectedLibrary.stidMissingInStidCodeCount }} missing in STID<br>
                                        {{ selectedLibrary.stidMissingInCommonLibCodeCount }} missing in Common Library<br>
                                        {{ selectedLibrary.stidInBothButDifferentCodeCount }} in both but different<br>
                                    </td>
                                </tr>
                                <tr>
                                    <th>STID sync date</th>
                                    <td v-if="selectedLibrary.maxStidSyncDate">
                                        {{ longDate(selectedLibrary.minStidSyncDate) }} (oldest current)<br>
                                        {{ longDate(selectedLibrary.maxStidSyncDate) }} (newest current)<br>
                                    </td>
                                    <td v-else>
                                        <i>Never</i>
                                    </td>
                                </tr>
                                <tr>
                                    <th>Statistics update date</th>
                                    <td>
                                        {{ longDate(selectedLibrary.minStatisticsUpdatedDate) }} (oldest current)<br>
                                        {{ longDate(selectedLibrary.maxStatisticsUpdatedDate) }} (newest current)<br>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
                <div class="column is-two-thirds-desktop is-full-tablet is-full-mobile">
                    <div
                        class="box">
                        <table class="table is-striped is-narrow is-fullwidth">
                            <tbody>
                                <tr>
                                    <th>LibraryMetadata.IsGrouped</th>
                                    <td>{{ selectedLibraryMetadata?.IsGrouped }}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
            <h3 class="title is-4">
                <span>Library sets ({{ selectedLibrary.codeSetCount }})</span>
            </h3>
            <details-table
                table-height="70vh"
                :loading-details="loadingDetails"
                :details="selectedLibraryDetails" />
        </div>
    </div>
</template>

<style scoped>
    .legend-box {
        display: inline-block;
        width: 1.5em;
        height: 1.5em;
        margin-right: 0.5em;
        border: 1px solid #efefef;
    }
    :deep(.not-release-ready) {
        background-color: #ff8080;
        color: white;
    }
</style>
